import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  TextField,
  Typography,
} from '@mui/material';
import { CalendarToday as CalendarIcon } from '@mui/icons-material';
import axios from '../../config/axios';
import { getApiUrl } from '../../config/environment';
import { useAuth } from '../../contexts/AuthContext';

export interface AppointmentScheduleDialogProps {
  open: boolean;
  onClose: () => void;
  onSaved?: () => void;
  patientId: string;
  patientName?: string;
  professionalId?: string;
  appointmentDate: string;
  appointmentType?: 'return' | 'consultation' | 'procedure' | 'exam' | 'emergency' | 'surgery' | 'courtesy';
  title?: string;
  /** Acima de modais grandes (ex.: evolução). */
  stackAboveAppModals?: boolean;
}

const STATUS_LABELS: Record<string, { label: string; bg: string; color: string }> = {
  scheduled: { label: 'Agendado', bg: '#e0e7ff', color: '#4338ca' },
  confirmed: { label: 'Confirmado', bg: '#dcfce7', color: '#15803d' },
  waiting: { label: 'Na espera', bg: '#fef3c7', color: '#d97706' },
  in_progress: { label: 'Em atendimento', bg: '#dbeafe', color: '#2563eb' },
  completed: { label: 'Concluído', bg: '#d1fae5', color: '#1d4ed8' },
  cancelled: { label: 'Cancelado', bg: '#fee2e2', color: '#dc2626' },
  no_show: { label: 'Não Compareceu', bg: '#fef3c7', color: '#b45309' },
  rescheduled: { label: 'Reagendar', bg: '#e0e7ff', color: '#3b82f6' },
};

function endTimeFromStartAndDuration(startTime: string, durationMinutes: number): string {
  const [h, m] = startTime.split(':').map(Number);
  const total = h * 60 + m + durationMinutes;
  const eh = Math.floor(total / 60) % 24;
  const em = total % 60;
  return `${String(eh).padStart(2, '0')}:${String(em).padStart(2, '0')}`;
}

const AppointmentScheduleDialog: React.FC<AppointmentScheduleDialogProps> = ({
  open,
  onClose,
  onSaved,
  patientId,
  patientName,
  professionalId: initialProfessionalId,
  appointmentDate,
  appointmentType = 'return',
  title,
  stackAboveAppModals = false,
}) => {
  const { user } = useAuth();
  const defaultProfessionalId = initialProfessionalId || user?.professional_id || '';

  const [form, setForm] = useState({
    appointment_date: appointmentDate,
    start_time: '09:00',
    duration_minutes: 30,
    professional_id: defaultProfessionalId,
    appointment_type: appointmentType,
    billing_mode: 'particular' as 'particular' | 'plano',
    title: title || (appointmentType === 'return' ? 'Retorno' : 'Consulta'),
    notes: '',
  });
  const [professionals, setProfessionals] = useState<any[]>([]);
  const [daySchedule, setDaySchedule] = useState<any[]>([]);
  const [loadingSchedule, setLoadingSchedule] = useState(false);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  const loadDaySchedule = useCallback(async (date: string) => {
    setLoadingSchedule(true);
    try {
      const { data: res } = await axios.get(getApiUrl('/clinic/healthcare-panel/day-schedule'), {
        params: { date },
      });
      setDaySchedule(res.success ? (res.data || []) : []);
    } catch {
      setDaySchedule([]);
    } finally {
      setLoadingSchedule(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    setForm({
      appointment_date: appointmentDate,
      start_time: '09:00',
      duration_minutes: 30,
      professional_id: defaultProfessionalId,
      appointment_type: appointmentType,
      billing_mode: 'particular',
      title: title || (appointmentType === 'return' ? 'Retorno' : 'Consulta'),
      notes: '',
    });
    void loadDaySchedule(appointmentDate);
    void axios.get(getApiUrl('/clinic/professionals'), { params: { is_active: true } })
      .then((res) => setProfessionals(res.data?.data || []))
      .catch(() => setProfessionals([]));
  }, [open, appointmentDate, appointmentType, defaultProfessionalId, loadDaySchedule, title]);

  const handleSave = async (override = false) => {
    if (!form.appointment_date || !form.start_time) return;
    setSaving(true);
    try {
      const payload: Record<string, unknown> = {
        patient_id: patientId,
        professional_id: form.professional_id || null,
        appointment_date: form.appointment_date,
        start_time: form.start_time,
        end_time: endTimeFromStartAndDuration(form.start_time, form.duration_minutes),
        duration_minutes: form.duration_minutes,
        appointment_type: form.appointment_type,
        billing_mode: form.billing_mode,
        title: form.title?.trim() || null,
        notes: form.notes?.trim() || null,
      };
      if (override) payload.override_availability = true;
      await axios.post(getApiUrl('/clinic/appointments'), payload);
      setSnackbar({ open: true, message: 'Consulta agendada com sucesso!', severity: 'success' });
      onSaved?.();
      window.setTimeout(onClose, 600);
    } catch (err: any) {
      if (err?.response?.status === 422 && err?.response?.data?.code === 'UNAVAILABLE' && !override) {
        const msg = err.response.data.error || 'Horário fora da grade do profissional.';
        if (window.confirm(`${msg}\n\nDeseja agendar mesmo assim (encaixe)?`)) {
          await handleSave(true);
          return;
        }
      } else {
        setSnackbar({
          open: true,
          message: err?.response?.data?.error || 'Erro ao agendar consulta',
          severity: 'error',
        });
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={saving ? undefined : onClose}
        maxWidth="sm"
        fullWidth
        sx={stackAboveAppModals ? { zIndex: (theme) => theme.zIndex.modal + 2 } : undefined}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 700 }}>
          <CalendarIcon sx={{ color: '#3b82f6' }} />
          Agendar consulta
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            {patientName && (
              <Typography variant="body2" sx={{ bgcolor: '#f0fdf4', p: 1.5, borderRadius: 2, border: '1px solid #d1fae5' }}>
                Paciente: <strong>{patientName}</strong>
              </Typography>
            )}
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Data"
                type="date"
                size="small"
                fullWidth
                required
                value={form.appointment_date}
                onChange={(e) => {
                  const next = e.target.value;
                  setForm((f) => ({ ...f, appointment_date: next }));
                  void loadDaySchedule(next);
                }}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="Horário"
                type="time"
                size="small"
                fullWidth
                required
                value={form.start_time}
                onChange={(e) => setForm((f) => ({ ...f, start_time: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Tipo</InputLabel>
                <Select
                  value={form.appointment_type}
                  label="Tipo"
                  onChange={(e) => setForm((f) => ({
                    ...f,
                    appointment_type: e.target.value as typeof f.appointment_type,
                  }))}
                >
                  <MenuItem value="consultation">Consulta</MenuItem>
                  <MenuItem value="return">Retorno</MenuItem>
                  <MenuItem value="procedure">Procedimento</MenuItem>
                  <MenuItem value="exam">Exame</MenuItem>
                  <MenuItem value="emergency">Urgência</MenuItem>
                  <MenuItem value="surgery">Cirurgia</MenuItem>
                  <MenuItem value="courtesy">Cortesia</MenuItem>
                </Select>
              </FormControl>
              <FormControl fullWidth size="small">
                <InputLabel>Duração</InputLabel>
                <Select
                  value={form.duration_minutes}
                  label="Duração"
                  onChange={(e) => setForm((f) => ({ ...f, duration_minutes: Number(e.target.value) }))}
                >
                  {[15, 30, 45, 60, 90, 120].map((m) => (
                    <MenuItem key={m} value={m}>{m} min</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <FormControl fullWidth size="small">
              <InputLabel>Profissional</InputLabel>
              <Select
                value={form.professional_id}
                label="Profissional"
                onChange={(e) => setForm((f) => ({ ...f, professional_id: e.target.value }))}
              >
                <MenuItem value="">Sem profissional</MenuItem>
                {professionals.map((p: any) => (
                  <MenuItem key={p.id} value={p.id}>
                    {p.full_name}{p.specialty ? ` — ${p.specialty}` : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Observações"
              size="small"
              fullWidth
              multiline
              rows={2}
              value={form.notes}
              onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
            />
            <Box>
              <Typography variant="caption" sx={{ fontWeight: 700, color: '#64748b', mb: 0.5, display: 'block' }}>
                Agenda do dia {loadingSchedule ? '(carregando…)' : `(${daySchedule.length} consulta(s))`}
              </Typography>
              <Box sx={{ maxHeight: 140, overflowY: 'auto', bgcolor: '#f8fafc', borderRadius: 2, p: 1 }}>
                {daySchedule.length === 0 ? (
                  <Typography variant="caption" sx={{ color: '#94a3b8' }}>Nenhuma consulta neste dia.</Typography>
                ) : (
                  daySchedule.map((apt: any) => {
                    const status = STATUS_LABELS[apt.status] || STATUS_LABELS.scheduled;
                    return (
                      <Box key={apt.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.3 }}>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: '#3b82f6', minWidth: 40 }}>
                          {apt.start_time?.substring(0, 5)}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#475569', flex: 1 }}>
                          {apt.clinic_patients?.full_name || 'Paciente'} — {apt.title || apt.appointment_type}
                        </Typography>
                        <Chip
                          label={status.label}
                          size="small"
                          sx={{ bgcolor: status.bg, color: status.color, fontSize: '0.55rem', height: 16 }}
                        />
                      </Box>
                    );
                  })
                )}
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={onClose} disabled={saving} sx={{ textTransform: 'none', color: '#64748b' }}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={() => void handleSave()}
            disabled={saving || !form.appointment_date || !form.start_time}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
            sx={{ bgcolor: '#3b82f6', '&:hover': { bgcolor: '#2563eb' }, textTransform: 'none', fontWeight: 600 }}
          >
            {saving ? 'Agendando…' : 'Confirmar agendamento'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default AppointmentScheduleDialog;
